import React, { useState } from 'react';
import { ThreadedPost, Thread, PublishedPost } from '../hooks/usePublishedPosts';
import { PostCard } from './PostCard';
import './ThreadView.css';

export interface ThreadViewProps {
  thread: Thread;
  onReply?: (post: PublishedPost) => void;
  onQuote?: (post: PublishedPost) => void;
  onDelete?: (post: PublishedPost) => void;
}

interface ThreadedPostViewProps {
  post: ThreadedPost;
  onReply?: (post: PublishedPost) => void;
  onQuote?: (post: PublishedPost) => void;
  onDelete?: (post: PublishedPost) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  replyCount?: number;
}

const ThreadedPostView: React.FC<ThreadedPostViewProps> = ({
  post,
  onReply,
  onQuote,
  onDelete,
  isCollapsed = false,
  onToggleCollapse,
  replyCount = 0,
}) => {
  const isNested = post.depth > 0;
  const hasReplies = post.replies.length > 0;
  
  // Flatten all nested replies into a single level
  const flattenReplies = (posts: ThreadedPost[]): ThreadedPost[] => {
    const result: ThreadedPost[] = [];
    const flatten = (p: ThreadedPost) => {
      result.push(p);
      p.replies.forEach(flatten);
    };
    posts.forEach(flatten);
    return result;
  };
  
  const allReplies = hasReplies ? flattenReplies(post.replies) : [];

  return (
    <div className={`threaded-post ${isNested ? 'threaded-post-nested' : 'threaded-post-root'}`}>
      <PostCard
        post={post}
        variant="full"
        showActions={true}
        onReply={onReply}
        onQuote={onQuote}
        onDelete={onDelete}
        isThreaded={isNested}
      />
      {hasReplies && !isNested && (
        <button 
          className="thread-toggle-button"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand thread' : 'Collapse thread'}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="14" 
            height="14" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          <span>{isCollapsed ? `Show ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}` : 'Hide replies'}</span>
        </button>
      )}
      {hasReplies && !isCollapsed && !isNested && (
        <div className="thread-replies">
          {allReplies.map((reply) => (
            <div key={reply.id} className="threaded-post threaded-post-nested">
              <PostCard
                post={reply}
                variant="full"
                showActions={true}
                onReply={onReply}
                onQuote={onQuote}
                onDelete={onDelete}
                isThreaded={true}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ThreadView: React.FC<ThreadViewProps> = ({ thread, onReply, onQuote, onDelete }) => {
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed by default
  
  // Count total replies in thread
  const countReplies = (post: ThreadedPost): number => {
    return post.replies.reduce((count, reply) => count + 1 + countReplies(reply), 0);
  };
  
  const replyCount = countReplies(thread.root);
  
  return (
    <div className="thread-container">
      <ThreadedPostView
        post={thread.root}
        onReply={onReply}
        onQuote={onQuote}
        onDelete={onDelete}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        replyCount={replyCount}
      />
    </div>
  );
};
